/**
 * i18n Initialization
 *
 * Configures react-i18next with:
 * - Lazy-loaded translation files from /locales/{lng}/{ns}.json
 * - Browser language detection with localStorage persistence
 * - Namespace splitting per UI area (common, booking, schedule, manage, auth, validation, email)
 * - Fallback chain: user preference → browser language → English
 *
 * Developer guide:
 *   1. Use `const { t } = useTranslation('namespace')` in components
 *   2. Add English keys to `public/locales/en/{namespace}.json`
 *   3. Other languages fall back to English until translated
 *   4. For formatting (dates, currency, numbers), use `useLocale()` from LocaleContext
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import HttpBackend from 'i18next-http-backend';
import LanguageDetector from 'i18next-browser-languagedetector';

/**
 * Supported languages.
 * Add new languages here and create matching directory in public/locales/
 */
// Anjas Studio ist deutschsprachig. Englisch bleibt als Rückfallebene für
// einzelne fehlende Schlüssel (fallbackLng unten). Die früher mitgelieferten
// Sprachen th/es/hi/pt/ban waren unvollständig übersetzt — dort erschienen
// rohe Schlüssel wie "nameLabel" statt Text.
export const SUPPORTED_LANGUAGES = [
  { code: 'de', name: 'German', nativeName: 'Deutsch', flag: '🇩🇪' },
  { code: 'en', name: 'English', nativeName: 'English', flag: '🇺🇸' },
] as const;

export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number]['code'];

/**
 * Translation namespaces — each maps to a JSON file per language.
 * Keep namespaces aligned with UI areas so only relevant strings are loaded.
 */
export const NAMESPACES = [
  'common',      // Shared: nav, buttons, roles, status labels, reference data labels
  'booking',     // Booking flow: modal, payment, add-ons, confirmation
  'schedule',    // Schedule view, class cards, class details
  'manage',      // Studio management: dashboard, settings, members, offerings
  'auth',        // Login, register, password reset
  'validation',  // Form errors and validation messages
  'email',       // Email template strings
  'group',       // Private group booking links
] as const;

/**
 * For Balinese (not in CLDR), we map to Indonesian for Intl formatting.
 * This is used by LocaleContext for date/number/currency formatting.
 */
export const INTL_LOCALE_MAP: Record<string, string> = {
  de: 'de-CH', // Schweizer Formate: Datum, Uhrzeit, CHF
};

/** Intl-compatible locale code for a given app language. */
export function getIntlLocale(language: string): string {
  return INTL_LOCALE_MAP[language] ?? language;
}

const defaultLanguage = import.meta.env.VITE_DEFAULT_LANGUAGE as SupportedLanguage | undefined;

const useForcedDefaultLanguage = Boolean(defaultLanguage);

i18n
  .use(HttpBackend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    // Fallback to English when a German translation key is missing
    fallbackLng: 'en',

    // Fixed default for this deployment (Anja test instance)
    lng: defaultLanguage,

    // Only allow languages we have translations for
    supportedLngs: SUPPORTED_LANGUAGES.map(l => l.code),

    // Namespace configuration
    ns: [...NAMESPACES],
    defaultNS: 'common',

    // Load translation files from public/locales/{lng}/{ns}.json
    backend: {
      loadPath: '/locales/{{lng}}/{{ns}}.json',
    },

    // Forced default: only ?lng= in URL overrides. Otherwise always Deutsch.
    // Normal deploys: URL → saved preference → browser → HTML tag.
    detection: useForcedDefaultLanguage
      ? {
          order: ['querystring'],
          lookupQuerystring: 'lng',
          caches: ['localStorage'],
        }
      : {
          order: ['querystring', 'localStorage', 'navigator', 'htmlTag'],
          lookupQuerystring: 'lng',
          lookupLocalStorage: 'tandava-language',
          caches: ['localStorage'],
        },

    interpolation: {
      // React already escapes values — no double-escaping
      escapeValue: false,
    },

    // Don't wait for all translations to load before rendering
    // Components will use Suspense or show English fallback
    react: {
      useSuspense: false,
    },
  });

if (defaultLanguage) {
  document.documentElement.lang = defaultLanguage;
}

export default i18n;
